/**
 * Dropdown panel showing the user's notifications.
 * Positioned below the bell icon, with "mark all read" action.
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '@/hooks/useNotifications';
import { useAuthContext } from '@/app/providers/AuthProvider';
import { markNotificationRead, markAllRead } from '@/lib/notifications';
import { EmptyState, LoadingSkeleton } from '@ezhard/ui';
import { NotificationItem } from './NotificationItem';
import type { Notification } from '@/types/models';
import styles from './NotificationPanel.module.css';

interface NotificationPanelProps {
  /** Whether the panel is visible. */
  open: boolean;
  /** Called to close the panel. */
  onClose: () => void;
}

export function NotificationPanel({ open, onClose }: NotificationPanelProps) {
  const { t } = useTranslation('settings');
  const { user } = useAuthContext();
  const { notifications, unreadCount, loading } = useNotifications();
  const navigate = useNavigate();

  const handleMarkAllRead = useCallback(async () => {
    if (!user) return;
    await markAllRead(user.id);
  }, [user]);

  const handleNotificationClick = useCallback(
    async (notification: Notification) => {
      if (!user) return;

      // Mark as read
      if (!notification.read) {
        await markNotificationRead(user.id, notification.id);
      }

      // Navigate to related entity if available
      if (notification.relatedEntityType && notification.relatedEntityId) {
        const entityType = notification.relatedEntityType;
        const entityId = notification.relatedEntityId;

        switch (entityType) {
          case 'space':
            navigate(`/spaces/${entityId}`);
            break;
          case 'shipment':
            navigate('/shipments');
            break;
          case 'manifest':
            navigate('/manifests');
            break;
          case 'item':
            navigate(`/inventory/items/${entityId}`);
            break;
          default:
            break;
        }
      }

      onClose();
    },
    [user, navigate, onClose],
  );

  if (!open) return null;

  return (
    <>
      {/* Invisible overlay to close panel on outside click */}
      <div
        className={styles.overlay}
        onClick={onClose}
        aria-hidden="true"
      />

      <div className={styles.panel} role="dialog" aria-label={t('notifications')}>
        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.headerTitle}>{t('notifications')}</h2>
          <button
            className={styles.markAllButton}
            onClick={handleMarkAllRead}
            disabled={unreadCount === 0}
            type="button"
          >
            {t('markAllRead')}
          </button>
        </div>

        {/* Notification list */}
        <div className={styles.list}>
          {loading ? (
            <div className={styles.loadingWrapper}>
              <LoadingSkeleton variant="rect" height="60px" count={3} />
            </div>
          ) : notifications.length === 0 ? (
            <div className={styles.emptyWrapper}>
              <EmptyState
                icon="notifications"
                title={t('noNotifications')}
                description={t('noNotificationsDescription')}
              />
            </div>
          ) : (
            notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onClick={handleNotificationClick}
              />
            ))
          )}
        </div>
      </div>
    </>
  );
}
